/**
 * Residential address helpers for Singapore vs overseas Profile editing.
 *
 * Business rule: staff who work in Singapore can only have an overseas
 * residential address in Malaysia (e.g. Johor commute). No other country.
 */

/** Sole allowed overseas residential country for this organisation. */
export const OVERSEAS_RESIDENTIAL_COUNTRY = 'Malaysia';

/** Pass types whose self-service address changes appear in the HR morning digest. */
export const WORK_PASS_ADDRESS_DIGEST_PASS_TYPES = ['ep', 's_pass', 'wp'];

/**
 * True only for Employment Pass, S Pass, and Work Permit.
 * LTVP, DP, student, visit, citizen, and PR are excluded.
 *
 * @param {string} [passType]
 * @returns {boolean}
 */
export function isWorkPassResidentialAddressDigestEligible(passType) {
  return WORK_PASS_ADDRESS_DIGEST_PASS_TYPES.includes(
    String(passType || '').trim().toLowerCase()
  );
}

/**
 * Extra success-toast sentence after a self-service save.
 * Empty unless the API confirmed an event was queued.
 *
 * @param {boolean} queued
 * @returns {string}
 */
export function residentialAddressDigestToastNote(queued) {
  return queued
    ? 'HR will see this address change in the next morning digest.'
    : '';
}

/**
 * Infer whether an existing address should open in Singapore or Overseas mode.
 *
 * @param {{ address?: string, postalCode?: string, location?: string, country?: string }} params
 * @returns {'singapore' | 'overseas'}
 */
export function inferResidentialAddressLocation({
  address = '',
  postalCode = '',
  location = '',
  country = '',
} = {}) {
  const stored = String(location || '').toLowerCase().trim();
  if (stored === 'singapore' || stored === 'overseas') {
    return stored;
  }

  const addr = String(address || '').trim();
  const postal = String(postalCode || '').trim().replace(/\s+/g, '');
  const countryName = String(country || '').trim();

  if (countryName && /^malaysia$/i.test(countryName)) {
    return 'overseas';
  }

  if (countryName && !/^singapore$/i.test(countryName)) {
    // Legacy non-MY country on file — still open overseas so HR can correct to Malaysia
    return 'overseas';
  }

  if (!addr && !postal) {
    return 'singapore';
  }

  if (/singapore/i.test(addr)) {
    return 'singapore';
  }

  if (/^\d{6}$/.test(postal)) {
    return 'singapore';
  }

  if (addr || (postal && !/^\d{6}$/.test(postal))) {
    return 'overseas';
  }

  return 'singapore';
}

/**
 * Try to recover Malaysia from the end of a free-text address.
 *
 * @param {string} address
 * @returns {string} 'Malaysia' or ''
 */
export function extractCountryFromAddress(address) {
  const addr = String(address || '').trim();
  if (!addr) return '';
  if (/(?:^|,\s*|\s+)Malaysia\s*$/i.test(addr)) {
    return OVERSEAS_RESIDENTIAL_COUNTRY;
  }
  return '';
}

/**
 * Ensure overseas free-text address ends with Malaysia.
 *
 * @param {string} address
 * @param {string} [country=OVERSEAS_RESIDENTIAL_COUNTRY]
 * @returns {string}
 */
export function ensureCountryInAddress(address, country = OVERSEAS_RESIDENTIAL_COUNTRY) {
  const addr = String(address || '').trim();
  const countryName = String(country || OVERSEAS_RESIDENTIAL_COUNTRY).trim();
  if (!addr || !countryName) return addr;

  if (new RegExp(`${countryName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'i').test(addr)) {
    return addr;
  }

  return `${addr.replace(/[,\s]+$/, '')}, ${countryName}`;
}

/**
 * Field copy for overseas (Malaysia-only) residential address.
 *
 * @returns {{
 *   addressPlaceholder: string,
 *   addressHelp: string,
 *   postalLabel: string,
 *   postalPlaceholder: string,
 *   postalHelp: string,
 *   unitLabel: string,
 *   unitPlaceholder: string,
 *   unitHelp: string,
 * }}
 */
export function getOverseasAddressFieldCopy() {
  return {
    // Pos Malaysia / UPU order (unlike Singapore): unit → building → street → area → city
    addressPlaceholder:
      'e.g. Pan Vista Condo, Jalan Permas 7, Bandar Baru Permas Jaya, Masai, Johor',
    addressHelp:
      'Malaysia order: building / condo name first, then street, taman / area, and city. Put the unit (e.g. A08-06) in Unit — not in this line.',
    postalLabel: 'Postcode',
    postalPlaceholder: 'e.g. 81100',
    postalHelp: 'Malaysia postcodes are 5 digits (e.g. 81100, 81750)',
    unitLabel: 'Unit / apartment',
    unitPlaceholder: 'e.g. A08-06 or B-21-E5',
    unitHelp: 'For condo / apartment only — leave blank for landed houses',
  };
}
